import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 30_000, withCredentials: true });

/**
 * Long-running calls instance — 5 minute timeout.
 *
 * Use for endpoints that legitimately take >30s:
 *   - AI try-on generation (Gemini Nano Banana, 8–30s typical, up to 60s)
 *   - R2 storage migration (1–10 min)
 *   - Image recompression (1–5 min)
 *   - Bulk email broadcast (5–60s)
 *   - Big Excel exports
 *
 * Default `api` keeps the 30s timeout — anything slower than that on a normal
 * read endpoint is a bug. Bypassing the dedupe layer too, since these are
 * mutations / long-lived ops where dedupe would silently swallow retries.
 */
export const apiLong = axios.create({ baseURL: API, timeout: 5 * 60_000, _skipDedupe: true, withCredentials: true });

/**
 * Global GET dedupe + short-TTL cache.
 *
 * Why: Components mount independently (separate widgets in the same tree,
 * React strict-mode double-invoke in dev) and each issues its own GET.
 * On the homepage we saw `/categories` × 3, `/departments` × 3, etc.
 * Most pages use raw `axios.get(...)` rather than our `api` instance,
 * so dedupe must be installed on BOTH the shared `api` instance and the
 * default `axios` instance to actually collapse those duplicates.
 *
 * Two lightweight tricks:
 *   1. In-flight promise dedupe: if a GET to the exact same URL is
 *      pending, return the existing promise.
 *   2. Tiny in-memory cache with a short TTL (default 2s): if two
 *      components fire the same GET ~1s apart we serve the second from
 *      cache. Long-lived caching is dangerous because the user expects
 *      fresh data after edits — 2s is short enough to be safe.
 *
 * Caveats:
 *   • Only applies to GETs. POST/PUT/DELETE/PATCH always go through.
 *   • Skipped when the call sets `_skipDedupe: true` in the request config.
 *   • Skipped when the URL has explicit cache-busting `?_=…`.
 *
 * Manual invalidation: import `invalidateApiCache(urlSubstring)` and call
 * it after a mutation that would change a cached endpoint's response.
 * Mutations through our wrapper auto-invalidate the matching base URL.
 */
const _inFlight = new Map();   // url → Promise<{data,headers}>
const _cache = new Map();      // url → { data, headers, exp }
const CACHE_TTL_MS = 2000;

function urlKey(config) {
    const base = (config.baseURL || "").replace(/\/$/, "");
    const path = config.url || "";
    const full = path.startsWith("http") ? path : `${base}${path.startsWith("/") ? "" : "/"}${path}`;
    if (config.params && Object.keys(config.params).length) {
        const qs = new URLSearchParams(config.params).toString();
        return `${full}${full.includes("?") ? "&" : "?"}${qs}`;
    }
    return full;
}

function shouldDedupe(config) {
    if (!config) return false;
    if ((config.method || "get").toLowerCase() !== "get") return false;
    if (config._skipDedupe) return false;
    if (config.responseType && config.responseType !== "json") return false; // blobs etc.
    return true;
}

function installDedupe(instance) {
    instance.interceptors.request.use((config) => {
        if (!shouldDedupe(config)) return config;
        const key = urlKey(config);
        if (key.includes("_=")) return config; // explicit cache-bust
        // Fresh cache hit → short-circuit with the cached response object
        const cached = _cache.get(key);
        if (cached && cached.exp > Date.now()) {
            config.adapter = () => Promise.resolve({
                data: cached.data,
                status: 200,
                statusText: "OK (cached)",
                headers: cached.headers || {},
                config,
                request: null,
            });
            return config;
        }
        // In-flight dedupe → reuse the existing promise via a custom adapter
        const pending = _inFlight.get(key);
        if (pending) {
            config.adapter = () => pending.then(({ data, headers }) => ({
                data, status: 200, statusText: "OK (deduped)",
                headers: headers || {}, config, request: null,
            }));
            return config;
        }
        // No pending → install a deferred promise we resolve in the response interceptor
        let resolveFn, rejectFn;
        const promise = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });
        // iter333 (Feb 28 2026) — Mark the deferred Promise as handled by
        // attaching a no-op .catch(). Without this, when no second
        // concurrent caller arrives during the in-flight window (the
        // typical case), the Promise stored in `_inFlight` is created
        // → rejected by `_dedupeReject` → ORPHAN rejected, which fires
        // either `unhandledrejection` OR the synchronous window 'error'
        // path. CRA + webpack-dev-server's overlay client hooks BOTH,
        // and even when the OUTER axios.get().catch() handles the
        // rejection, the inner orphan still surfaces the overlay.
        // This .catch is purely a handled-marker — real awaiters (line
        // 95 adapter `pending.then(...)`) still see the rejection via
        // their own chain because Promise resolution is multi-cast.
        promise.catch(() => { /* iter333 — observe orphan dedupe rejections */ });
        _inFlight.set(key, promise);
        config._dedupeKey = key;
        config._dedupeResolve = resolveFn;
        config._dedupeReject = rejectFn;
        return config;
    });

    instance.interceptors.response.use((response) => {
        const cfg = response.config || {};
        const key = cfg._dedupeKey;
        if (key) {
            // Cache fresh data only on true 2xx (status ranges configured by
            // validateStatus may resolve 4xx — those must NOT be cached but
            // they MUST still release the in-flight dedupe lock).
            if (response.status >= 200 && response.status < 300) {
                _cache.set(key, { data: response.data, headers: response.headers, exp: Date.now() + CACHE_TTL_MS });
            }
            if (cfg._dedupeResolve) cfg._dedupeResolve({ data: response.data, headers: response.headers });
            _inFlight.delete(key);
        }
        // Invalidate cache for matching collection on mutations.
        // POST /categories          → invalidate "/categories"
        // PUT/DELETE /items/abc-123 → invalidate "/items"
        // PUT /admin/settings       → invalidate "/admin/settings"
        const method = (cfg.method || "get").toLowerCase();
        if (method !== "get" && cfg.url) {
            const path = cfg.url.split("?")[0];
            const segs = path.replace(/^https?:\/\/[^/]+/, "").split("/").filter(Boolean);
            // Drop last segment if it looks like an id (uuid/objectid/numeric/long)
            if (segs.length && /^([0-9a-f-]{6,}|\d+)$/i.test(segs[segs.length - 1])) segs.pop();
            const collection = "/" + segs.join("/");
            if (collection.length > 1) invalidateApiCache(collection);
        }
        return response;
    }, (error) => {
        const cfg = (error && error.config) || {};
        const key = cfg._dedupeKey;
        if (key) {
            if (cfg._dedupeReject) cfg._dedupeReject(error);
            _inFlight.delete(key);
        }
        return Promise.reject(error);
    });
}

installDedupe(api);
installDedupe(axios);

export function invalidateApiCache(urlSubstring = "") {
    if (!urlSubstring) { _cache.clear(); _inFlight.clear(); return; }
    for (const k of Array.from(_cache.keys())) if (k.includes(urlSubstring)) _cache.delete(k);
    for (const k of Array.from(_inFlight.keys())) if (k.includes(urlSubstring)) _inFlight.delete(k);
}

export const fmtINR = (n) => {
    if (n === null || n === undefined || isNaN(n)) return "₹0";
    return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

export const fmtDate = (d) => {
    if (!d) return "";
    try {
        const dt = new Date(d);
        return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return d;
    }
};

export const todayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export default api;
